/**
 * <TutorPrepareLivePage> — « Préparer mon live » (page).
 *
 * Le legacy : `tutor/lives/PrepareLiveScreen.js` (route `tutor/prepare-live`, ouverte par la
 * tâche « Prépare ton live » de l'accueil — `TutorTaskBanner`). Les lives du jour et du lendemain,
 * une carte par live (`<TutorPrepareSessionCard>`) : pour chaque élève, le chapitre ou la matière
 * qui permettra à Sam de démarrer la séance avec lui.
 *
 * En tête, Sam dit où en est la préparation, avec les mots de l'ancien écran (`getWelcomeMessage`) :
 * aucun live à préparer, tant d'élèves à préparer, plus que tant, tout est prêt. Le compte est
 * celui de toutes les cartes — un élève qu'un checkpoint prend en charge n'y entre pas.
 *
 * Contrôlée : les lives, leur état et la boîte des chapitres (élève visé, programme ouvert)
 * arrivent en props.
 */
import React from "react"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { ChampionSays } from "../../molecules/ChampionSays"
import { Header } from "../../molecules/Header"
import { TutorPrepareSessionCard, preparationStats } from "../../organisms/TutorPrepareSessionCard"
import { PageTemplate } from "../../templates/PageTemplate"
import { TutorRevisionProgramSheet } from "../../templates/TutorRevisionProgramSheet"
import { type UISubject } from "../../../ports/primary/ui/types"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UITutorPrepareOccurrence, type UITutorPrepareStudent } from "../../../ports/primary/ui/types/tutorLives"
import { type UITutorProgramChapter, type UITutorStudyProgram } from "../../../ports/primary/ui/types/tutorEleves"
import { TutorPrepareLiveSkeleton } from "./TutorPrepareLiveSkeleton"

/** La colonne d'une carte de live : elle se lit d'un regard, même au bureau. */
const PREPARE_CONTENT_WIDTH = 640

const TITLE = "Préparation des sessions"

/** Ce que Sam dit de la préparation — `getWelcomeMessage` de l'ancien écran, sans ses émojis. */
function preparationMessage(occurrences: UITutorPrepareOccurrence[]): string {
    const { done, total } = occurrences.reduce(
        (sum, occurrence) => {
            const stats = preparationStats(occurrence)
            return { done: sum.done + stats.done, total: sum.total + stats.total }
        },
        { done: 0, total: 0 }
    )
    if (total === 0) return "Il n'y a pas de sessions live à préparer pour aujourd'hui."
    if (done === 0) {
        return `Il y a ${countLabel(total, "élève")} à préparer. Sélectionne les chapitres et matières pour faciliter le démarrage de la session !`
    }
    if (done < total) {
        return `Super ! Il ne te reste plus que ${countLabel(total - done, "élève")} en attente de préparation. Continue comme ça !`
    }
    return "Félicitations ! Tu as préparé tous tes élèves. Tu es prêt(e) pour tes sessions !"
}

export type TutorPrepareLivePageProps = {
    occurrences: UITutorPrepareOccurrence[]
    subjects: UISubject[]
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    onClose?: () => void
    onChooseChapter?: (occurrence: UITutorPrepareOccurrence, student: UITutorPrepareStudent) => void
    onSelectSubject?: (
        occurrence: UITutorPrepareOccurrence,
        student: UITutorPrepareStudent,
        subjectId: string | number
    ) => void
    /** La boîte des chapitres : l'élève visé, ses programmes. `null` : fermée. */
    chapterPicker?: {
        student: UITutorPrepareStudent
        programs: UITutorStudyProgram[]
        selectedProgramId?: UITutorStudyProgram["id"]
        loading?: boolean
        /** La lecture des programmes a échoué : son message ; « Réessayer » relit. */
        error?: string | null
        onRetry?: () => void
    } | null
    onSelectProgram?: (programId: UITutorStudyProgram["id"]) => void
    onSelectChapter?: (chapter: UITutorProgramChapter) => void
    onCloseChapterPicker?: () => void
    testID?: string
}

export function TutorPrepareLivePage(props: TutorPrepareLivePageProps) {
    const {
        occurrences,
        subjects,
        loading = false,
        error,
        onRetry,
        onClose,
        onChooseChapter,
        onSelectSubject,
        chapterPicker,
        onSelectProgram,
        onSelectChapter,
        onCloseChapterPicker,
        testID,
    } = props

    const header = (
        <Header
            title={TITLE}
            left={<IconButton iconName="times" appearance="transparent" iconColor="primary" onPress={onClose} />}
        />
    )

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<TutorPrepareLiveSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PREPARE_CONTENT_WIDTH}
        >
            <Stack gap="5">
                <ChampionSays message={preparationMessage(occurrences)} />
                {occurrences.map((occurrence) => (
                    <TutorPrepareSessionCard
                        key={occurrence.id}
                        occurrence={occurrence}
                        subjects={subjects}
                        onChooseChapter={(student) => onChooseChapter?.(occurrence, student)}
                        onSelectSubject={(student, subjectId) => onSelectSubject?.(occurrence, student, subjectId)}
                    />
                ))}
            </Stack>
            <TutorRevisionProgramSheet
                visible={chapterPicker != null}
                onClose={() => onCloseChapterPicker?.()}
                mode="pick"
                programs={chapterPicker?.programs ?? []}
                selectedProgramId={chapterPicker?.selectedProgramId}
                onSelectProgram={(id) => onSelectProgram?.(id)}
                onPickChapter={(chapter) => onSelectChapter?.(chapter)}
                loading={chapterPicker?.loading}
                error={chapterPicker?.error}
                onRetry={chapterPicker?.onRetry}
            />
        </PageTemplate>
    )
}
